/** Simulations: a named set of workloads priced together over a period, kept under a link, no account.
 *
 *  /simulations lists the ones this browser made (their ids and edit keys, in localStorage) and opens one from
 *  a link or a file. /simulation?s=<id>[&edit=<key>] is one simulation: the edit link can change it, the share
 *  link can only read it, and either can be copied as a .json file and opened again anywhere. Every figure comes
 *  from the server (tools.simulate); the page only edits the document and draws the answer. */
import { useEffect, useRef, useState } from 'react'
import { BASE, api } from '../api'
import { modesOn, useSettings } from '../settings'
import type { CatalogModel, HostingMode, HostingRow, OptimiseUseCase, Provider, RegionChoices, SimulationDoc, SimulationRecord, SimulationWorkload, Simulated } from '../types'
import { RegionPicker, regionLabel, regionsFor, useRegions } from './RegionPicker'
import { TimeStep, growthWords, usePeriod, type Period } from './TimeStep'
import { ErrorNote, Help, ModelSelect, ProviderTag, compact, usd } from './ui'
import { ExportPdf } from './ExportPdf'
import { Useful } from './Useful'
import { useAsync, useDebounced } from './useAsync'
import { track } from './track'

type Go = (v: 'simulations' | 'simulation' | 'calculator' | 'compare' | 'assessment') => void
const MINE = 'simulations'
const MODE_LABEL: Record<HostingMode, string> = { on_demand: 'per token', self_hosted: 'self-hosted', dedicated: 'dedicated cluster' }

/** What this browser keeps of each simulation it made: enough to list it and to edit it again. */
export interface Mine { id: string; edit?: string; name: string; client: string; updated_at: number; total_usd?: number; months?: number; workloads?: number }
export function readMine(): Mine[] {
  try { const v = JSON.parse(localStorage.getItem(MINE) ?? '[]'); return Array.isArray(v) ? v : [] } catch { return [] }
}
function writeMine(list: Mine[]) { try { localStorage.setItem(MINE, JSON.stringify(list.slice(0, 50))) } catch { /* storage blocked */ } }
function remember(m: Mine) { writeMine([m, ...readMine().filter((x) => x.id !== m.id)]) }

export const fresh = (regions: RegionChoices, period: Period): SimulationDoc => ({
  name: '', client: '', regions, months: period.months, growth_pct: period.end ? 0 : period.growth,
  end_output_tokens_per_day: period.end ?? null, notes: '',
  workloads: [{ label: 'Workload 1', canonical: 'openai/gpt-oss-120b', output_tokens_per_day: 2_000_000, input_output_ratio: 3,
    options: [{ provider: 'oci', mode: 'on_demand' }, { provider: 'aws', mode: 'on_demand' }], choice: 0 }],
})

/** The link's parts: /simulation?s=<id>&edit=<key>, or a pasted link or id. */
function parseLink(text: string): { id: string; edit?: string } | null {
  const t = text.trim()
  if (!t) return null
  try {
    const u = new URL(t, window.location.origin)
    const id = u.searchParams.get('s')
    if (id) return { id, edit: u.searchParams.get('edit') ?? undefined }
  } catch { /* not a URL */ }
  return /^[A-Za-z0-9_-]{6,20}$/.test(t) ? { id: t } : null
}
export const shareUrl = (id: string) => `${window.location.origin}${BASE}simulation?s=${encodeURIComponent(id)}`
export const editUrl = (id: string, key: string) => `${shareUrl(id)}&edit=${encodeURIComponent(key)}`

/** A document as a file: the document alone, named, so it opens again anywhere. */
function download(doc: SimulationDoc) {
  const blob = new Blob([JSON.stringify({ llmopex_simulation: 1, doc }, null, 2)], { type: 'application/json' })
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = `${(doc.name || 'simulation').replace(/[^\w.-]+/g, '-').slice(0, 60)}.json`
  a.click()
  setTimeout(() => URL.revokeObjectURL(a.href), 1000)
  track('simulation_file', 'export')
}
function readFile(file: File): Promise<SimulationDoc> {
  return file.text().then((text) => {
    const v = JSON.parse(text)
    const doc = v?.doc ?? v
    if (!doc || !Array.isArray(doc.workloads) || !doc.workloads.length) throw new Error('That file is not a simulation.')
    track('simulation_file', 'import')
    return doc as SimulationDoc
  })
}

/** A pill that opens a short menu under itself; closes on a click anywhere else. */
function Menu({ label, children, disabled, title }: { label: string; children: React.ReactNode; disabled?: boolean; title?: string }) {
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLSpanElement>(null)
  useEffect(() => {
    if (!open) return
    const away = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('mousedown', away)
    return () => document.removeEventListener('mousedown', away)
  }, [open])
  return (
    <span className="simmenu" ref={box}>
      <button type="button" className="pill" aria-expanded={open} aria-haspopup="menu" disabled={disabled} title={title} onClick={() => setOpen(!open)}>{label} ▾</button>
      {open ? <span className="menu" role="menu" onClick={(e) => { if ((e.target as HTMLElement).closest('.menurow:not(.keep)')) setOpen(false) }}>{children}</span> : null}
    </span>
  )
}

/** The chosen options month by month: the total as a bold line, each workload as a thin one. Figures are the
 *  server's; this draws them. */
function SimChart({ p }: { p: Simulated }) {
  const W = 760, H = 220, L = 62, R = 14, T = 12, B = 30
  const n = p.months
  const lines = [{ label: 'All workloads, chosen options', v: p.totals.monthly_usd, bold: true },
    ...p.workloads.filter((w) => w.chosen.available && w.chosen.monthly_usd).map((w) => ({ label: w.label, v: w.chosen.monthly_usd!, bold: false }))]
  const max = Math.max(1, ...lines.flatMap((l) => l.v))
  const pow = 10 ** Math.floor(Math.log10(max))
  const top = ([1, 2, 2.5, 5, 10].map((k) => k * pow).find((v) => v >= max) ?? 10 * pow)
  const x = (m: number) => L + (n > 1 ? (m * (W - L - R)) / (n - 1) : 0)
  const y = (c: number) => T + (1 - c / top) * (H - T - B)
  const every = n > 24 ? 6 : n > 12 ? 4 : 2
  const colours = ['#7c3aed', '#0ea5e9', '#f43f5e', '#10b981', '#f59e0b', '#6366f1', '#ec4899', '#14b8a6']
  return (
    <div className="overtime simchart">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Monthly cost over ${n} months`}>
        {[0, 1, 2, 3, 4].map((k) => (
          <g key={k}><line x1={L} x2={W - R} y1={y((top * k) / 4)} y2={y((top * k) / 4)} className="grid" />
            <text x={L - 8} y={y((top * k) / 4) + 4} textAnchor="end">{usd((top * k) / 4, 0)}</text></g>
        ))}
        {Array.from({ length: n }, (_, m) => m).filter((m) => m === n - 1 || (m % every === 0 && n - 1 - m > every / 2)).map((m) => (
          <text key={m} x={x(m)} y={H - 8} textAnchor={m === n - 1 ? 'end' : m === 0 ? 'start' : 'middle'}>month {m + 1}</text>
        ))}
        {lines.map((l, i) => (
          <path key={i} d={l.v.map((c, m) => `${m ? 'L' : 'M'}${x(m).toFixed(1)} ${y(c).toFixed(1)}`).join(' ')} fill="none"
            stroke={l.bold ? 'var(--ink)' : colours[(i - 1) % colours.length]} strokeWidth={l.bold ? 2.5 : 1.5} strokeOpacity={l.bold ? 1 : 0.85} />
        ))}
      </svg>
      <div className="legend muted small">
        {lines.map((l, i) => <span key={i}><i style={{ background: l.bold ? 'var(--ink)' : colours[(i - 1) % colours.length] }} /> {l.label}</span>)}
      </div>
    </div>
  )
}

/** Alerts: whoever holds the edit link can name an address to be told when the total moves after a refresh. */
function WatchForm({ id, edit }: { id: string; edit: string }) {
  const [tick, setTick] = useState(0)
  const list = useAsync(() => api.simulationWatches(id, edit).catch(() => null), [id, edit, tick])
  const [email, setEmail] = useState('')
  const [trap, setTrap] = useState('')
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState('')
  const add = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.trim()) { setNote('Enter an email address.'); return }
    setBusy(true); setNote('')
    try { await api.watchSimulation(id, email.trim(), edit, trap); setEmail(''); setTick((t) => t + 1); track('simulation_watch', 'set') }
    catch (err) { setNote(err instanceof Error ? err.message : String(err)) }
    finally { setBusy(false) }
  }
  const remove = async (addr: string) => { setBusy(true); try { await api.unwatchSimulation(id, addr, edit); setTick((t) => t + 1) } finally { setBusy(false) } }
  return (
    <div className="simwatch">
      <b className="small">Alerts</b>
      <span className="muted small">Prices refresh every few hours; name an address to be told when this simulation's total moves by 1% or more. Each email carries a link to stop.</span>
      {list.data?.mail === false ? <span className="note small">This server cannot send mail, so alerts are off here.</span> : (
        <form className="row" onSubmit={add}>
          <input type="email" value={email} placeholder="name@company.com" maxLength={254} autoComplete="email" onChange={(e) => setEmail(e.target.value)} disabled={busy} />
          <input className="trap" type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" value={trap} onChange={(e) => setTrap(e.target.value)} />
          <button type="submit" className="pill" disabled={busy}>Watch</button>
          {note ? <span className="err small" role="alert">{note}</span> : null}
        </form>
      )}
      {list.data?.watches.length ? (
        <div className="muted small">Watching: {list.data.watches.map((w) => (
          <span key={w.email} className="simwatcher">{w.email}{w.notified_at ? ' · told once' : ''} <button type="button" className="linklike small" disabled={busy} onClick={() => void remove(w.email)}>stop</button></span>
        ))}</div>
      ) : null}
    </div>
  )
}

// ---- the list --------------------------------------------------------------------------------------------------

export function SimulationsPage({ go }: { go: Go }) {
  const [mine, setMine] = useState<Mine[]>(readMine)
  const [link, setLink] = useState('')
  const [error, setError] = useState('')
  const [copied, setCopied] = useState('')
  const file = useRef<HTMLInputElement>(null)
  const open = (id: string, edit?: string) => {
    window.history.pushState(null, '', edit ? editUrl(id, edit) : shareUrl(id))
    go('simulation')
  }
  const openLink = () => {
    const p = parseLink(link)
    if (!p) { setError('Paste a simulation link, or its id.'); return }
    open(p.id, p.edit)
  }
  const openFile = async (f: File | undefined) => {
    if (!f) return
    try {
      const doc = await readFile(f)
      sessionStorage.setItem('simulation-import', JSON.stringify(doc))
      window.history.pushState(null, '', `${BASE}simulation`)
      go('simulation')
    } catch (e) { setError(e instanceof Error ? e.message : String(e)) }
  }
  const copy = async (m: Mine) => {
    try { await navigator.clipboard.writeText(shareUrl(m.id)); setCopied(m.id); setTimeout(() => setCopied(''), 1500) } catch { window.prompt('Copy this link', shareUrl(m.id)) }
  }
  const forget = (m: Mine) => {
    if (!window.confirm(`Forget "${m.name || 'Untitled simulation'}" on this browser? It stays under its link.`)) return
    const next = mine.filter((x) => x.id !== m.id); writeMine(next); setMine(next)
  }
  return (
    <>
      <section className="panel">
        <h2 className="row">Simulations<Help text="A simulation is several workloads and models priced together over a period, with an option chosen for each and one total. It is kept under a link: the edit link changes it, the share link reads it, and a file exported from it opens again anywhere. No account." />
          <span className="spacer" />
          <button type="button" className="pill action" onClick={() => { window.history.pushState(null, '', `${BASE}simulation`); go('simulation') }}>+ New simulation</button>
        </h2>
        <p className="muted small">A client's whole situation priced at once: several workloads and models, one period, one total. Kept under a link, no sign-in.</p>
        {mine.length ? (
          <div className="simcards">
            {mine.map((m) => (
              <div className="simcard" key={m.id}>
                <h3>{m.name || 'Untitled simulation'}</h3>
                <div className="muted small">{m.client ? `${m.client} · ` : ''}{m.workloads ?? '?'} workload{m.workloads === 1 ? '' : 's'} · {m.months ?? '?'} months</div>
                {m.total_usd != null ? <div className="simtotal">{usd(m.total_usd, 0)} <span className="muted small">over the period</span></div> : null}
                <div className="muted small">edited {new Date(m.updated_at * 1000).toLocaleDateString()}{m.edit ? '' : ' · shared with you, read only'}</div>
                <div className="simcardbar">
                  <button type="button" className="pill action" onClick={() => open(m.id, m.edit)}>Open</button>
                  <button type="button" className="pill" onClick={() => void copy(m)}>{copied === m.id ? 'copied' : 'Share link'}</button>
                  <span className="spacer" />
                  <button type="button" className="linklike small" onClick={() => forget(m)}>forget</button>
                </div>
              </div>
            ))}
          </div>
        ) : <p className="muted small">None on this browser yet.</p>}
      </section>
      <section className="panel">
        <h2>Continue where you were<Help text="A simulation made on another machine, or before this browser's data was cleared: open it from the edit link you kept, or from the .json file you exported." /></h2>
        <div className="simcontinue">
          <div className="simway">
            <h3>From a link</h3>
            <p className="muted small">Paste the edit link to carry on, or a share link to read.</p>
            <div className="row">
              <input type="text" value={link} placeholder="https://…/simulation?s=…&edit=…" onChange={(e) => { setLink(e.target.value); setError('') }}
                onKeyDown={(e) => { if (e.key === 'Enter') openLink() }} />
              <button type="button" className="pill action" onClick={openLink}>Open</button>
            </div>
          </div>
          <div className="simway">
            <h3>From a file</h3>
            <p className="muted small">The .json a simulation page exported, from any machine.</p>
            <button type="button" className="pill" onClick={() => file.current?.click()}>Choose a .json file</button>
            <input ref={file} type="file" accept="application/json,.json" style={{ display: 'none' }} onChange={(e) => void openFile(e.target.files?.[0])} />
          </div>
        </div>
        {error ? <p className="err small" role="alert">{error}</p> : null}
      </section>
    </>
  )
}

/** Your simulations, as a panel on the Price page's second stage: every one this browser kept (or was handed a
 *  link to), with Open / Share / Remove, a link to paste, a .json to open, and a new one. The list is the
 *  browser's own; the edit link is the only way to change a simulation. */
export function MinePanel({ go, onNew, onOpen, onImport, tick = 0 }: { go: Go; onNew: () => void; onOpen?: (id: string, edit?: string) => void; /** a .json was read: the page takes the document */ onImport?: (doc: SimulationDoc) => void; /** bumped when a simulation was kept, so the list reads the browser again */ tick?: number }) {
  const [mine, setMine] = useState<Mine[]>(readMine)
  useEffect(() => { setMine(readMine()) }, [tick])
  const [link, setLink] = useState('')
  const [asking, setAsking] = useState(false)
  const [error, setError] = useState('')
  const [copied, setCopied] = useState('')
  const file = useRef<HTMLInputElement>(null)
  const open = (id: string, edit?: string) => { if (onOpen) { onOpen(id, edit); return } window.history.pushState(null, '', edit ? editUrl(id, edit) : shareUrl(id)); go('simulation') }
  const openLink = () => { const p = parseLink(link); if (!p) { setError('Paste a simulation link, or its id.'); return } open(p.id, p.edit) }
  const openFile = async (f?: File) => {
    if (!f) return
    try { const doc = await readFile(f); if (onImport) { onImport(doc); return } sessionStorage.setItem('simulation-import', JSON.stringify(doc)); window.history.pushState(null, '', `${BASE}simulation`); go('simulation') }
    catch (e) { setError(e instanceof Error ? e.message : String(e)) }
  }
  const copy = async (m: Mine) => {
    try { await navigator.clipboard.writeText(shareUrl(m.id)); setCopied(m.id); setTimeout(() => setCopied(''), 1500) } catch { window.prompt('Copy this link', shareUrl(m.id)) }
  }
  const forget = (m: Mine) => {
    if (!window.confirm(`Remove "${m.name || 'Untitled simulation'}" from this browser's list? It stays under its link.`)) return
    const next = mine.filter((x) => x.id !== m.id); writeMine(next); setMine(next)
  }
  const when = (t: number) => { const d = new Date(t * 1000), now = new Date(); return d.toDateString() === now.toDateString() ? `today ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : d.toLocaleDateString([], { day: 'numeric', month: 'short' }) }
  return (
    <section className="minepanel unfold">
      {asking ? (
        <div className="row" style={{ marginTop: 8 }}>
          <input type="text" value={link} placeholder="https://…/simulation?s=…&edit=…" style={{ flex: '1 1 320px' }} onChange={(e) => { setLink(e.target.value); setError('') }}
            onKeyDown={(e) => { if (e.key === 'Enter') openLink() }} />
          <button type="button" className="pill action" onClick={openLink}>Open</button>
        </div>
      ) : null}
      {error ? <p className="err small" role="alert">{error}</p> : null}
      {mine.length ? (
        <div className="table-wrap simtable">
          <table>
            <thead><tr><th>Name</th><th>Client</th><th>Workloads</th><th className="num">Over the period</th><th>Updated</th><th></th></tr></thead>
            <tbody>
              {mine.map((m) => (
                <tr key={m.id}>
                  <td><b>{m.name || 'Untitled simulation'}</b> {m.edit ? <span className="tag">edit link</span> : <span className="tag plain">shared with you</span>}</td>
                  <td>{m.client || '—'}</td>
                  <td>{m.workloads ?? '?'} · {m.months ?? '?'} months</td>
                  <td className="num">{m.total_usd != null ? usd(m.total_usd, 0) : '—'}</td>
                  <td className="muted small">{when(m.updated_at)}</td>
                  <td><div className="row" style={{ justifyContent: 'flex-end', flexWrap: 'nowrap' }}>
                    <button type="button" className="pill action" onClick={() => open(m.id, m.edit)}>Open</button>
                    <button type="button" className="pill" onClick={() => void copy(m)}>{copied === m.id ? 'copied' : 'Share'}</button>
                    <button type="button" className="pill" onClick={() => forget(m)}>Remove</button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <p className="muted small" style={{ margin: '0 0 6px' }}>None kept in this browser yet.</p>}
      <div className="row small">
        <button type="button" className="linklike" onClick={() => setAsking((a) => !a)}>Open a link…</button>
        <span className="muted">·</span>
        <button type="button" className="linklike" onClick={() => file.current?.click()}>Import .json</button>
        <input ref={file} type="file" accept="application/json,.json" style={{ display: 'none' }} onChange={(e) => void openFile(e.target.files?.[0])} />
        <span className="muted">·</span>
        <button type="button" className="linklike" onClick={onNew}>+ New simulation</button>
        <span className="spacer" />
        <span className="muted">kept under a link, no account: the edit link is in this browser; anyone with the share link can read</span>
      </div>
    </section>
  )
}

// ---- one simulation --------------------------------------------------------------------------------------------

export type Model = { canonical: string; family: string; label: string }
type Cell = { usd: number | null; reason?: string; detail?: HostingRow['detail'] }
const okey = (p: Provider, m: HostingMode) => `${p}:${m}`

/** The cloud-and-mode options a workload can be priced on, each with its month-1 cost: the Calculator's own
 *  per-vendor call, one per vendor. A mode a cloud cannot use for the model is left out of the row. */
function useWorkloadCells(w: SimulationWorkload, regions: Record<string, string> | undefined, vendors: Provider[]) {
  const d = useDebounced(w, 400)
  return useAsync<Record<string, Cell>>(async () => {
    const cells: Record<string, Cell> = {}
    await Promise.all(vendors.map(async (p) => {
      try {
        const r = await api.vendorHosting({ canonical: d.canonical, provider: p, output_tokens_per_day: d.output_tokens_per_day,
          input_output_ratio: d.input_output_ratio, precision: d.precision || undefined, regions })
        r.results.forEach((row) => { cells[okey(p, row.mode)] = { usd: row.available ? (row.monthly_usd ?? null) : null, reason: row.reason, detail: row.available ? row.detail : undefined } })
      } catch { /* that vendor stays blank */ }
    }))
    return cells
  }, [d.canonical, d.output_tokens_per_day, d.input_output_ratio, d.precision, regions, vendors.join(',')])
}

export function WorkloadCard({ i, w, models, regions, vendors, readOnly, onChange, onRemove, extra, hint, detail, detailOpen }: {
  i: number; w: SimulationWorkload; doc: SimulationDoc; models: Model[] | null; regions: Record<string, string> | undefined
  vendors: Provider[]; readOnly: boolean; onChange: (w: SimulationWorkload) => void; onRemove?: () => void
  /** what a page adds to the card's fields, after the volume (Optimise: the use case and the context) */
  extra?: React.ReactNode
  /** what the option chips mean on this page (Simulate: options to compare; Optimise: what runs today) */
  hint?: string
  /** what unfolds under the card (the Calculator for this workload), rendered only while open */
  detail?: () => React.ReactNode; detailOpen?: boolean
}) {
  const settings = useSettings()
  const [open, setOpen] = useState(Boolean(detailOpen))
  const cells = useWorkloadCells(w, regions, vendors)
  const picked = (p: Provider, m: HostingMode) => w.options.findIndex((x) => x.provider === p && x.mode === m)
  // an option the model cannot use any more (another model was chosen) leaves the picks
  useEffect(() => {
    if (!cells.data || readOnly) return
    const keep = w.options.filter((o) => cells.data![okey(o.provider, o.mode)]?.usd != null)
    if (keep.length !== w.options.length) {
      const chosen = w.options[w.choice]
      const at = keep.findIndex((o) => o.provider === chosen?.provider && o.mode === chosen?.mode)
      onChange({ ...w, options: keep.length ? keep : w.options, choice: at >= 0 ? at : 0 })
    }
  }, [cells.data]) // eslint-disable-line react-hooks/exhaustive-deps
  const click = (p: Provider, m: HostingMode) => {
    const at = picked(p, m)
    if (at >= 0 && at === w.choice) { const opts = w.options.filter((_, k) => k !== at); if (opts.length) onChange({ ...w, options: opts, choice: 0 }) }
    else if (at >= 0) onChange({ ...w, choice: at })
    else if (w.options.length < 3) onChange({ ...w, options: [...w.options, { provider: p, mode: m }], choice: w.options.length })
    else onChange({ ...w, options: [...w.options.slice(1), { provider: p, mode: m }], choice: 2 })
  }
  const rows = vendors.filter((p) => !cells.data || modesOn(settings, p).some((m) => cells.data![okey(p, m)]?.usd != null))
  return (
    <div className="simworkload">
      <div className="simwhead">
        <span className="simnum">{i + 1}</span>
        <input type="text" className="simlabel" value={w.label} placeholder={`Workload ${i + 1}`} maxLength={80} readOnly={readOnly} onChange={(e) => onChange({ ...w, label: e.target.value })} />
        <span className="spacer" />
        {onRemove && !readOnly ? <button type="button" className="linklike small" onClick={onRemove}>remove</button> : null}
      </div>
      <div className="simfields">
        <div className="field"><label>Model</label>
          {models ? <ModelSelect models={models} value={w.canonical} minWidth={260} render={(m) => m.label} onChange={(c) => { if (!readOnly) onChange({ ...w, canonical: c }) }} />
            : <span className="muted small">models…</span>}
        </div>
        <div className="field"><label>Output tokens a day</label>
          <input type="number" min={1} step={100000} value={w.output_tokens_per_day} readOnly={readOnly}
            onChange={(e) => onChange({ ...w, output_tokens_per_day: Math.max(1, Number(e.target.value) || 1) })} />
        </div>
        <div className="field"><label>Input tokens per output token</label>
          <input type="number" min={0} step={1} value={w.input_output_ratio} readOnly={readOnly}
            onChange={(e) => onChange({ ...w, input_output_ratio: Math.max(0, Number(e.target.value) || 0) })} />
        </div>
        <div className="field"><label>A month, at these volumes</label>
          <span className="simvol">{compact(w.output_tokens_per_day * 30)} out · {compact(w.output_tokens_per_day * w.input_output_ratio * 30)} in</span>
        </div>
        {extra}
      </div>
      <div className="simoptlabel small">
        <b>{hint ? 'Where it runs today' : 'Options to price'}</b> <span className="muted">— {hint ?? "each chip is this workload's month-1 cost on that cloud and mode. Click up to three to compare them; the one with the tick is counted in the total, the others stay as alternatives."}{!cells.data ? ' Pricing…' : ''}</span>
      </div>
      <div className="optrows simoptrows">
        {rows.map((p) => (
          <div className="optrow" key={p}>
            <div className="rowhdr"><ProviderTag provider={p} /></div>
            <div className="modechips">
              {modesOn(settings, p).map((m) => {
                const cell = cells.data?.[okey(p, m)]
                if (cells.data && (!cell || cell.usd == null)) return null           // not offered for this model
                const at = picked(p, m)
                const chosen = at >= 0 && at === w.choice
                const unit = cell?.detail && m !== 'on_demand' ? (m === 'dedicated' ? `${cell.detail.instance} · ${cell.detail.ai_units} AU` : cell.detail.instance) : null
                return (
                  <button type="button" key={m} className={`chip${chosen ? ' chosen' : ''}`} aria-pressed={at >= 0} disabled={readOnly}
                    title={chosen ? 'Counted in the total; click to drop it' : at >= 0 ? 'An alternative; click to count it instead' : 'Click to price this option'}
                    onClick={() => click(p, m)}>
                    {chosen ? <i className="dot" /> : null}
                    <span className="mlabel">{MODE_LABEL[m]}</span>
                    <span className="cost">{cell?.usd != null ? `${usd(cell.usd, 0)}/mo` : '…'}</span>
                    {unit ? <span className="unitname" title={unit}>{unit}</span> : null}
                  </button>
                )
              })}
            </div>
          </div>
        ))}
      </div>
      {cells.data && !rows.length ? <p className="note small">No cloud can price this model at all: it is sold nowhere per token and cannot be self-hosted. Pick another.</p> : null}
      {detail ? (
        <details className="carddetail" open={open} onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)} data-tour="case-details">
          <summary>Details: these options side by side, the break-even, and this workload over time</summary>
          {open ? <div className="calcin">{detail()}</div> : null}
        </details>
      ) : null}
    </div>
  )
}

/** The two fields Optimise reads on a workload: what it is for, and how long a request usually is. */
export function UseCaseFields({ w, onChange }: { w: SimulationWorkload; onChange: (w: SimulationWorkload) => void }) {
  const useCases = useAsync(() => api.optimiseUseCases().catch(() => [] as OptimiseUseCase[]), [])
  const uc = useCases.data?.find((u) => u.id === (w.use_case ?? 'chat'))
  return (
    <>
      <div className="field"><label>Use case<Help text="What the workload is for. It decides which savings levers may be offered: batch and flex only where nobody waits, caching only where a prompt repeats, a smaller model only where the client can test one." /></label>
        <select value={w.use_case ?? 'chat'} onChange={(e) => onChange({ ...w, use_case: e.target.value })} title={uc?.note}>
          {(useCases.data ?? [{ id: 'chat', label: 'Chat assistant' } as OptimiseUseCase]).map((u) => <option key={u.id} value={u.id}>{u.label}</option>)}
        </select>
      </div>
      <div className="field"><label>Tokens per request<Help text="How long a request usually is, input included. Left blank, the use case's usual size; given, and well above it, trimming the request is offered as a saving." /></label>
        <input type="number" min={100} step={1000} value={w.context_tokens ?? ''} placeholder={String(uc?.context_tokens ?? '')}
          onChange={(e) => onChange({ ...w, context_tokens: e.target.value ? Math.max(100, Number(e.target.value) || 100) : null })} />
      </div>
    </>
  )
}

/** What a card's Details is given: where the case is priced, its period, and the ways back into the case. */
export type DetailContext = { regions: Record<string, string>; period: Period; onPeriod: (next: Period) => void; onVolume: (output_tokens_per_day: number) => void }

/** A case: the Price page. One line says what it is (name, client, where, how long) and holds My cases and
 *  Save / Share / Export; the workloads are cards on the left, each unfolding the Calculator for itself; the
 *  result on the right has two tabs, Cost and Savings. One document throughout, kept under a link. */
export function SimulationPage({ go, seed, onDoc, onSaved, initialTab = 'cost', openList = false, onNew, onOpenCase, savings, detail, model, onModel }: {
  go: Go
  seed?: SimulationDoc; onDoc?: (doc: SimulationDoc) => void
  /** called after a save */
  onSaved?: () => void
  initialTab?: 'cost' | 'savings'; openList?: boolean
  onNew?: () => void; onOpenCase?: (id: string, edit?: string) => void
  /** the Savings tab's content for the document, and a card's Details for its workload */
  savings?: (doc: SimulationDoc) => React.ReactNode
  detail?: (w: SimulationWorkload, i: number, ctx: DetailContext) => React.ReactNode
  /** the model the visitor picked elsewhere (the menu's search): the first workload takes it; and the way back */
  model?: string; onModel?: (canonical: string) => void
}) {
  const settings = useSettings()
  const [regions, setRegions] = useRegions()
  const [period, setPeriod] = usePeriod()
  const [{ id, edit }, setLink] = useState(() => {
    const q = new URLSearchParams(window.location.search)
    const sid = q.get('s') ?? ''
    const key = q.get('edit') ?? readMine().find((m) => m.id === sid)?.edit
    return { id: sid, edit: key ?? undefined }
  })
  const [doc, setDoc] = useState<SimulationDoc | null>(() => {
    if (id) return null                                     // fetched below
    if (seed) return seed
    try {
      const imported = sessionStorage.getItem('simulation-import')
      if (imported) { sessionStorage.removeItem('simulation-import'); return JSON.parse(imported) as SimulationDoc }
    } catch { /* storage blocked */ }
    return fresh(regions, period)
  })
  const [loadError, setLoadError] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [savedAt, setSavedAt] = useState<number | null>(null)
  const [dirty, setDirty] = useState(false)
  const [copied, setCopied] = useState('')
  const file = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (!id || doc) return
    api.getSimulation(id).then((r: SimulationRecord) => {
      setDoc(r.doc); setSavedAt(r.updated_at)
      if (edit) remember({ id, edit, name: r.doc.name, client: r.doc.client, updated_at: r.updated_at, total_usd: r.priced.totals.total_usd, months: r.doc.months, workloads: r.doc.workloads.length })
    }).catch((e) => {
      const why = e instanceof Error ? e.message : String(e)
      // a case that is gone from the server leaves this browser's list too, so it is not offered again
      if (/no such/i.test(why)) writeMine(readMine().filter((m) => m.id !== id))
      setLoadError(why)
    })
  }, [id]) // eslint-disable-line react-hooks/exhaustive-deps
  const readOnly = Boolean(id && !edit)
  const change = (next: SimulationDoc) => { if (readOnly) return; setDoc(next); setDirty(true) }
  useEffect(() => { if (doc) onDoc?.(doc) }, [doc]) // eslint-disable-line react-hooks/exhaustive-deps
  const [tab, setTab] = useState<'cost' | 'savings'>(initialTab)
  const [listOpen, setListOpen] = useState(openList)
  const [setupOpen, setSetupOpen] = useState(false)
  const [tick, setTick] = useState(0)
  const first = doc?.workloads[0]?.canonical
  useEffect(() => { if (first) onModel?.(first) }, [first]) // eslint-disable-line react-hooks/exhaustive-deps
  const seenModel = useRef(model)
  useEffect(() => {
    if (model === seenModel.current) return
    seenModel.current = model
    if (model && doc && !readOnly && doc.workloads[0] && doc.workloads[0].canonical !== model) {
      change({ ...doc, workloads: doc.workloads.map((w, j) => (j === 0 ? { ...w, canonical: model } : w)) })
    }
  }, [model]) // eslint-disable-line react-hooks/exhaustive-deps

  const models = useAsync<Model[]>(async () => {
    const [priced, catalogue] = await Promise.all([api.models(), api.searchModels({ limit: 2000 }).catch(() => [] as CatalogModel[])])
    const seen = new Set(priced.map((m) => m.canonical))
    return [...priced.map((m) => ({ canonical: m.canonical, family: m.family, label: m.label })),
      ...catalogue.filter((m) => !seen.has(m.canonical)).map((m) => ({ canonical: m.canonical, family: m.family, label: `${m.canonical.split('/').pop()} · self-host only` }))]
  }, [])
  const debounced = useDebounced(doc, 400)
  const priced = useAsync<Simulated | null>(() => (debounced ? api.priceSimulation(debounced) : Promise.resolve(null)), [debounced])
  const p = priced.data
  const vendors = settings.vendors
  const regionBody = doc?.regions ? (doc.regions as Record<string, string>) : regionsFor(regions, vendors)

  const save = async () => {
    if (!doc) return
    setSaving(true); setSaveError('')
    try {
      if (id && edit) {
        const r = await api.updateSimulation(id, doc, edit)
        setSavedAt(r.updated_at); setDirty(false)
        remember({ id, edit, name: doc.name, client: doc.client, updated_at: r.updated_at, total_usd: r.priced.totals.total_usd, months: doc.months, workloads: doc.workloads.length })
        onSaved?.(); setTick((t) => t + 1)
      } else {
        const r = await api.createSimulation(doc)
        const now = Date.now() / 1000
        remember({ id: r.id, edit: r.edit_key, name: doc.name, client: doc.client, updated_at: now, total_usd: p?.totals.total_usd, months: doc.months, workloads: doc.workloads.length })
        setLink({ id: r.id, edit: r.edit_key }); setSavedAt(now); setDirty(false)
        window.history.replaceState(null, '', editUrl(r.id, r.edit_key))
        onSaved?.(); setTick((t) => t + 1)
      }
    } catch (e) { setSaveError(e instanceof Error ? e.message : String(e)) }
    finally { setSaving(false) }
  }
  const copy = async (what: 'share' | 'edit') => {
    if (!id) return
    const url = what === 'edit' && edit ? editUrl(id, edit) : shareUrl(id)
    try { await navigator.clipboard.writeText(url); setCopied(what); setTimeout(() => setCopied(''), 1500) } catch { window.prompt('Copy this link', url) }
  }
  const fork = () => { if (!doc) return; setLink({ id: '', edit: undefined }); setDirty(true); setSavedAt(null); window.history.replaceState(null, '', `${BASE}simulation`) }
  const openFile = async (f: File | undefined) => {
    if (!f) return
    try { const d = await readFile(f); setLink({ id: '', edit: undefined }); setDoc(d); setDirty(true); setSavedAt(null); window.history.replaceState(null, '', `${BASE}simulation`) }
    catch (e) { setSaveError(e instanceof Error ? e.message : String(e)) }
  }

  if (loadError) return (
    <section className="panel">
      <h2>This simulation could not be opened</h2>
      <p className="muted small">{/no such/i.test(loadError) ? 'Nothing is kept under this link any more: it was removed with its edit link. It has been taken off this browser\'s list.' : loadError}</p>
      <div className="row"><button type="button" className="pill action" onClick={() => (onNew ? onNew() : go('simulations'))}>+ New simulation</button>
        <button type="button" className="pill" onClick={() => go('simulations')}>My simulations</button></div>
    </section>
  )
  if (!doc) return <section className="panel"><p className="muted small">opening…</p></section>

  const setWorkload = (i: number, w: SimulationWorkload) => change({ ...doc, workloads: doc.workloads.map((x, j) => (j === i ? w : x)) })
  const addWorkload = () => change({ ...doc, workloads: [...doc.workloads, { ...fresh(regions, period).workloads[0], label: `Workload ${doc.workloads.length + 1}`, use_case: 'chat' }] })
  const removeWorkload = (i: number) => change({ ...doc, workloads: doc.workloads.filter((_, j) => j !== i) })
  const docPeriod: Period = { months: doc.months, growth: doc.growth_pct, end: doc.end_output_tokens_per_day ?? null }
  const n = doc.months
  const pdf = (plain: boolean) => (
    <ExportPdf placeholder={doc.name || 'Hosting cost simulation'} disabled={p ? undefined : 'The simulation is being priced'}
      label={plain ? 'PDF for the client' : 'Export'} plain={plain}
      summary="The PDF has the client, the period and growth, the totals, and each workload with every option priced and the one chosen, with the price dates."
      build={(fields) => api.simulationReport({ doc, ...fields })} />
  )
  const status = readOnly ? <span className="tag plain">read only · shared with you</span>
    : savedAt && !dirty ? <span className="tag plain">saved</span> : id ? <span className="tag">unsaved changes</span> : <span className="tag plain">not kept yet</span>

  const choices = Object.values(regionBody)
  const where = choices.length && choices.every((c) => c === choices[0]) ? regionLabel(choices[0]) : 'a region per vendor'
  const onPeriod = (next: Period) => { setPeriod(next); change({ ...doc, months: next.months, growth_pct: next.end ? 0 : next.growth, end_output_tokens_per_day: next.end ?? null }) }

  return (
    <>
      <section className="panel caseline" data-tour="case-line">
        <input className="simname" type="text" value={doc.name} placeholder="Name this simulation" maxLength={120} readOnly={readOnly} onChange={(e) => change({ ...doc, name: e.target.value })} />
        {status}
        <span className="casesetup muted small">for <b>{doc.client || 'no client yet'}</b> · <b>{where}</b> · <b>{n} months, {growthWords(docPeriod, compact)}</b> ·{' '}
          <button type="button" className="linklike" aria-expanded={setupOpen} onClick={() => setSetupOpen((o) => !o)}>{setupOpen ? 'done' : 'change'}</button></span>
        <span className="spacer" />
        <button type="button" className="pill" aria-expanded={listOpen} onClick={() => setListOpen((o) => !o)}>My simulations {listOpen ? '▴' : '▾'}</button>
          {readOnly ? <button type="button" className="pill action" onClick={fork}>Copy to my simulations</button>
            : <button type="button" className="pill action" disabled={saving || (!dirty && Boolean(id))} onClick={() => void save()}>{saving ? 'saving…' : id ? 'Save' : 'Keep under a link'}</button>}
          <Menu label="Share" disabled={!id} title={id ? undefined : 'Keep it under a link first'}>
            <button type="button" className="menurow" onClick={() => void copy('share')}><b>Copy share link</b><span className="muted small">anyone with it can read; not change</span></button>
            {edit ? <button type="button" className="menurow" onClick={() => void copy('edit')}><b>Copy edit link</b><span className="muted small">keep it to yourself: it is the only way to edit</span></button> : null}
          </Menu>
          <input ref={file} type="file" accept="application/json,.json" style={{ display: 'none' }} onChange={(e) => void openFile(e.target.files?.[0])} />
          <Menu label="Export">
            <span className="keep">{pdf(true)}</span>
            <button type="button" className="menurow" onClick={() => download(doc)}><b>Simulation file (.json)</b><span className="muted small">to keep, or to open again on another machine</span></button>
          </Menu>
      </section>
      {copied ? <p className="muted small" role="status">{copied === 'edit' ? 'Edit link copied.' : 'Share link copied.'}</p> : null}
      <ErrorNote error={saveError} />
      {listOpen ? <MinePanel go={go} tick={tick} onNew={() => { setListOpen(false); onNew?.() }} onOpen={onOpenCase ? (cid, key) => { setListOpen(false); onOpenCase(cid, key) } : undefined}
        onImport={(d) => { setLink({ id: '', edit: undefined }); setDoc({ ...d, workloads: d.workloads.map((w) => ({ ...w, use_case: w.use_case ?? 'chat' })) }); setDirty(true); setSavedAt(null); setListOpen(false); window.history.replaceState(null, '', `${BASE}simulation`) }} /> : null}
      {setupOpen ? (
        <section className="panel setup simsetup casesetuppanel">
          <h3 className="step">Client<Help text="Who the simulation is for; printed with the name on the PDF." /></h3>
          <input type="text" className="simclient" value={doc.client} placeholder="Client (optional)" maxLength={120} readOnly={readOnly} onChange={(e) => change({ ...doc, client: e.target.value })} />
          <h3 className="step">Location<Help text="Where each vendor is priced, for every workload: in country, the cheapest region anywhere, or one region per vendor." /></h3>
          <RegionPicker regions={doc.regions ? { ...regions, ...(doc.regions as RegionChoices) } : regions} compact bare
            onChange={(r) => { setRegions(r); change({ ...doc, regions: regionsFor(r, vendors) }) }} />
          <h3 className="step">Time<Help text="The period and the growth of every workload's volume across it. Every month is priced at today's list prices." /></h3>
          <TimeStep value={docPeriod} onChange={onPeriod} />
        </section>
      ) : null}

      <div className="casecols">
        <div className="casecol" data-tour="case-workloads">
          <h3 className="coltitle">Workloads <span className="tag plain">{doc.workloads.length}</span></h3>
          <div className="simworkloads">
            {doc.workloads.map((w, i) => (
              <WorkloadCard key={i} i={i} w={w} doc={doc} models={models.data ?? null} regions={regionBody} vendors={vendors} readOnly={readOnly}
                onChange={(next) => setWorkload(i, next)} onRemove={doc.workloads.length > 1 ? () => removeWorkload(i) : undefined}
                extra={<UseCaseFields w={w} onChange={(next) => setWorkload(i, next)} />}
                detailOpen={i === 0 && !id && doc.workloads.length === 1}
                detail={detail ? () => detail(w, i, { regions: regionBody, period: docPeriod, onPeriod, onVolume: (v) => setWorkload(i, { ...w, output_tokens_per_day: v }) }) : undefined} />
            ))}
          </div>
          {!readOnly && doc.workloads.length < 8 ? <button type="button" className="caseadd" onClick={addWorkload}>+ Add a workload</button> : null}
        </div>

        <div className="casecol" data-tour="case-result">
          <h3 className="coltitle">Result <span className="muted small">{priced.loading ? 'pricing…' : `today's list prices · ${n} months`}</span></h3>
          <section className="panel datapanel caseresult">
            <div className="rtabs" role="tablist">
              {savings ? (<>
                <button type="button" role="tab" aria-selected={tab === 'cost'} onClick={() => setTab('cost')}>Cost</button>
                <button type="button" role="tab" aria-selected={tab === 'savings'} onClick={() => setTab('savings')} data-tour="case-savings">Savings</button>
              </>) : <b className="rtitle">The simulation, priced</b>}
              <span className="spacer" />
              {tab === 'cost' && p ? <Useful key={id || 'new'} page="simulation" /> : null}
              {tab === 'cost' ? pdf(false) : null}
            </div>
            {tab === 'cost' ? (<>
        <ErrorNote error={priced.error} />
        {p ? (
          <>
            <div className="kpis">
              <div className="kpi"><span className="k">Over {n} months</span><b>{usd(p.totals.total_usd, 0)}</b><span className="f">chosen options, {p.workloads.length} workload{p.workloads.length === 1 ? '' : 's'}</span></div>
              <div className="kpi"><span className="k">Month 1</span><b>{usd(p.totals.first_month_usd, 0)}</b><span className="f">{compact(p.workloads.reduce((a, w) => a + w.output_tokens_per_day.first, 0))} output tokens a day</span></div>
              <div className="kpi"><span className="k">Month {n}</span><b>{usd(p.totals.last_month_usd, 0)}</b><span className="f">{compact(p.workloads.reduce((a, w) => a + w.output_tokens_per_day.last, 0))} a day by then</span></div>
              <div className="kpi"><span className="k">Cheapest mix would be</span><b>{usd(p.totals.cheapest_mix_usd, 0)}</b><span className="f">{p.totals.saving_usd > 0.5 ? `${usd(p.totals.saving_usd, 0)} less than the chosen` : 'the chosen options are the cheapest'}</span></div>
            </div>
            {p.totals.unpriced.length ? <p className="note small">Not in the total: {p.totals.unpriced.join(', ')} — the chosen option cannot be priced for that model; pick another.</p> : null}
            {p.months > 1 ? <SimChart p={p} /> : null}
            <div className="table-wrap simtable">
              <table>
                <thead><tr><th>Workload</th><th>Option</th><th className="num">Month 1</th><th className="num">Month {n}</th><th className="num">Over {n} months</th><th className="cap">Capacity</th></tr></thead>
                <tbody>
                  {p.workloads.map((w) => w.options.map((o, k) => (
                    <tr key={`${w.index}-${k}`} className={k === w.choice ? 'chosen' : ''} style={{ cursor: readOnly ? undefined : 'pointer' }}
                      onClick={() => { if (!readOnly && o.available) setWorkload(w.index, { ...doc.workloads[w.index], choice: k }) }}
                      title={readOnly ? undefined : 'Click to make this the option the total counts'}>
                      {k === 0 ? <td rowSpan={w.options.length}><b>{w.label}</b><div className="muted small">{w.model}</div></td> : null}
                      <td>{k === w.choice ? <b>{o.label} ✓</b> : o.label}{w.cheapest === k && k !== w.choice ? <> <span className="tag plain">cheapest</span></> : null}</td>
                      {o.available ? (
                        <>
                          <td className="num">{usd(o.first_month_usd!, 0)}</td>
                          <td className="num">{usd(o.last_month_usd!, 0)}</td>
                          <td className="num"><b>{usd(o.total_usd!, 0)}</b></td>
                          <td className="muted small cap">{o.units == null ? 'billed per token'
                            : o.step_ups?.length ? `${o.step_ups[0].units} ${o.mode === 'dedicated' ? 'units' : 'instances'} from month ${o.step_ups[0].month}${o.step_ups.length > 1 ? `, ${o.step_ups[o.step_ups.length - 1].units} by month ${o.step_ups[o.step_ups.length - 1].month}` : ''}`
                            : `${o.units[0]} ${o.mode === 'dedicated' ? 'unit' : 'instance'}${o.units[0] === 1 ? '' : 's'} throughout`}</td>
                        </>
                      ) : <td colSpan={4} className="muted small">{o.reason ?? 'not available'}</td>}
                    </tr>
                  )))}
                </tbody>
              </table>
            </div>
            <p className="assumptions">{p.basis.charAt(0).toUpperCase() + p.basis.slice(1)}. Each workload is priced exactly as the Calculator prices it, with the egress and throughput assumptions it uses.</p>
            {id && edit ? <WatchForm id={id} edit={edit} /> : id ? null : <p className="muted small">Keep the simulation under a link to be told when its total moves after a price refresh.</p>}
          </>
        ) : priced.loading ? <p className="muted small">pricing…</p> : null}
            </>) : (savings ? savings(debounced ?? doc) : null)}
          </section>
        </div>
      </div>
    </>
  )
}
